import { useCallback, useEffect, useRef, useState } from 'react';

// the store module, not the flow barrel: the barrel carries the flow's dialogs.
import { useFlowStore } from '@/features/flow/store';
import {
    isV2VoucherCode,
    redeemV2Code,
    UNLOCK_COPY,
} from '@/features/flow/unlock';
import {
    useAnchorChain,
    useAuthedApi,
    useFactionsAuthedApi,
} from '@/features/wallet';
import { wolfswapFactionsApi } from '@/lib/api/clients/wolfswap';
import {
    confirmOnboardingReady,
    createOnboardingWallet,
    fetchOnboardingConfig,
    fetchOnboardingStatus,
    redeemOnboardingVoucher,
    rejoinOnboardingQueue,
    type OnboardingConfig,
    type OnboardingStatus,
} from '@/lib/api/onboarding';
import type { SelfSignedTradingSessionProof } from '@/lib/api/tradingSession';
import { describeErrorText } from '@/lib/errors';

const DEFAULT_STATUS: OnboardingStatus = {
    state: 'notRedeemed',
    position: null,
    usersAhead: null,
    estimatedWaitMinutes: null,
    readyExpiresAt: null,
    admissionExpiresAt: null,
    walletAddress: null,
    lastError: null,
    lastErrorCode: null,
    pollAfterMs: 60_000,
};

// Launch admission is on by default, so an unreachable config endpoint must not
// hand out wallets. It must not resurrect the gate after launch either, so the
// last answer the server actually gave wins over the default.
const LAST_ENABLED_KEY = 'snappy.onboarding.enabled';

function lastKnownEnabled(): boolean {
    try {
        return window.localStorage.getItem(LAST_ENABLED_KEY) !== 'false';
    } catch {
        return true;
    }
}

function rememberEnabled(enabled: boolean) {
    try {
        window.localStorage.setItem(LAST_ENABLED_KEY, String(enabled));
    } catch {
        // private-mode storage failures are not worth blocking onboarding over
    }
}

// A queued user polling on the slow tier could otherwise burn most of a two
// minute ready check before finding out it started.
const MAX_POLL_MS = { hot: 3_000, warm: 10_000, cold: 60_000 } as const;

function clampPollMs(status: OnboardingStatus): number {
    const ceiling =
        status.state === 'ready' ||
        status.state === 'admitted' ||
        status.state === 'provisioning'
            ? MAX_POLL_MS.hot
            : status.state === 'queued'
              ? MAX_POLL_MS.warm
              : MAX_POLL_MS.cold;
    return Math.min(status.pollAfterMs, ceiling);
}

const DEFAULT_CONFIG: OnboardingConfig = {
    enabled: true,
    usableLanes: 0,
};

export function useSnappyOnboarding(active: boolean) {
    const factionsApi = useFactionsAuthedApi();
    const moonHubApi = useAuthedApi();
    const { anchorChainId } = useAnchorChain();
    const [config, setConfig] = useState<OnboardingConfig>(() => ({
        ...DEFAULT_CONFIG,
        enabled: lastKnownEnabled(),
    }));
    const [configResolved, setConfigResolved] = useState(false);
    const [status, setStatus] = useState(DEFAULT_STATUS);
    // The queue row is a durable fact about where the user is in onboarding, so
    // the flow has to wait for the first read before deciding which step to
    // resume at. Anything that means "there is no queue to read" counts as read.
    const [statusSettled, setStatusSettled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const statusRef = useRef(status);
    statusRef.current = status;
    // Every write that sets the status outside the poll loop bumps this. A poll
    // that was already in flight when it happened is stale by definition, and
    // its payload still carries the pre-write `lastError` — which the provision
    // step would read as a fresh failure. Drop those answers instead.
    const writeSeqRef = useRef(0);

    const applyStatus = useCallback(
        (
            next:
                | OnboardingStatus
                | ((current: OnboardingStatus) => OnboardingStatus),
        ) => {
            writeSeqRef.current += 1;
            setStatus(next);
        },
        [],
    );

    const refreshConfig = useCallback(async () => {
        try {
            const next = await fetchOnboardingConfig(wolfswapFactionsApi);
            rememberEnabled(next.enabled);
            setConfig(next);
            if (!next.enabled)
                applyStatus({ ...DEFAULT_STATUS, state: 'disabled' });
        } catch {
            // Keep whatever the server last told us rather than snapping back to
            // the default: after launch that default would re-gate new users
            // during any factions outage.
            setConfig((current) => current);
        } finally {
            setConfigResolved(true);
        }
    }, [applyStatus]);

    const refreshStatus = useCallback(async () => {
        if (!factionsApi || !active || !config.enabled) return;
        const seq = writeSeqRef.current;
        try {
            const next = await fetchOnboardingStatus(factionsApi);
            if (seq !== writeSeqRef.current) return;
            setStatus(next);
            setError(null);
        } catch (nextError) {
            setError(
                describeErrorText(nextError, { operation: 'Wallet setup' }),
            );
        } finally {
            setStatusSettled(true);
        }
    }, [active, config.enabled, factionsApi]);

    useEffect(() => {
        // Nobody outside the gate needs this: demo mode, disconnected wallets and
        // existing Snappy Wallet holders would otherwise poll it forever.
        if (!active) return;
        void refreshConfig();
        const timer = window.setInterval(() => void refreshConfig(), 60_000);
        return () => window.clearInterval(timer);
    }, [active, refreshConfig]);

    // Restarting on state transitions matters: an action (redeem, ready, rejoin)
    // updates the status outside the poll loop, and the already-scheduled timer
    // still carries the previous state's cadence. Without the restart a fresh
    // redemption could sit on the old 60-second timer instead of the 10-second
    // queued cadence, and a ready check could go unnoticed for most of a minute.
    const queueState = status.state;
    useEffect(() => {
        if (!active || !config.enabled || !factionsApi) return;
        let stopped = false;
        let timer: number | undefined;
        const poll = async () => {
            await refreshStatus();
            if (!stopped) {
                timer = window.setTimeout(
                    () => void poll(),
                    clampPollMs(statusRef.current),
                );
            }
        };
        // Browsers throttle timers in hidden tabs to a minute or more, which is
        // long enough to sleep through a whole ready check. Re-sync the moment
        // the tab comes back rather than waiting out the throttled timer.
        const onVisible = () => {
            if (document.visibilityState !== 'visible' || stopped) return;
            if (timer !== undefined) window.clearTimeout(timer);
            void poll();
        };
        void poll();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            stopped = true;
            document.removeEventListener('visibilitychange', onVisible);
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [active, config.enabled, factionsApi, refreshStatus, queueState]);

    const perform = useCallback(
        async (action: () => Promise<OnboardingStatus>) => {
            setBusy(true);
            setError(null);
            try {
                const next = await action();
                applyStatus(next);
            } catch (nextError) {
                setError(
                    describeErrorText(nextError, { operation: 'Wallet setup' }),
                );
            } finally {
                setBusy(false);
            }
        },
        [applyStatus],
    );

    /**
     * A `V2` code is not a queue code (the queue refuses it): it is redeemed
     * through the Snappy Wallet admission, exactly as the feature-flags view
     * does, and the user is handed over to the browser wallet flow. The switch
     * is a full reload, after which the flow store lands on V2 and opens the
     * browser wallet setup. No queue status comes back from this path.
     */
    const redeemIntoV2 = useCallback(async (code: string) => {
        setBusy(true);
        setError(null);
        let handingOver = false;
        try {
            const result = await redeemV2Code(code);
            if (!result.ok) {
                setError(result.message);
                return;
            }
            const flow = useFlowStore.getState();
            // the switch is keyed by the resolved social identity; without one
            // there is nothing to switch, so say what is missing instead.
            if (!flow.accountHash) {
                setError(UNLOCK_COPY.signInFirst);
                return;
            }
            flow.switchTo('v2');
            // the reload is under way: keep the button busy until it lands.
            handingOver = flow.active !== 'v2';
        } finally {
            if (!handingOver) setBusy(false);
        }
    }, []);

    const redeem = useCallback(
        async (code: string) => {
            if (isV2VoucherCode(code)) {
                await redeemIntoV2(code);
                return;
            }
            if (!factionsApi) return;
            await perform(async () =>
                redeemOnboardingVoucher(factionsApi, code),
            );
        },
        [factionsApi, perform, redeemIntoV2],
    );

    /**
     * EOA path: take the lane but stop there. The wallet is created by the
     * provision step, which has to collect a signature first.
     */
    const confirmReadyOnly = useCallback(async () => {
        if (!factionsApi) return;
        await perform(async () =>
            statusRef.current.state === 'admitted'
                ? statusRef.current
                : confirmOnboardingReady(factionsApi),
        );
    }, [factionsApi, perform]);

    /**
     * EOA path: the self-signed statement is the create authority. Throws so the
     * provisioning ceremony can report which leg failed; the existing poll loop
     * reports the deployment landing.
     */
    const startEoaWalletCreation = useCallback(
        async (proof: SelfSignedTradingSessionProof) => {
            if (!moonHubApi) {
                throw new Error(
                    'Sign in with your wallet to set up an account.',
                );
            }
            const started = await createOnboardingWallet(moonHubApi, {
                tradingSessionProof: proof,
                anchorChainId,
            });
            applyStatus((current) => ({
                ...current,
                state:
                    started.status === 'completed'
                        ? 'completed'
                        : 'provisioning',
                walletAddress: started.walletAddress,
                pollAfterMs: started.status === 'completed' ? 60_000 : 3_000,
            }));
        },
        [anchorChainId, applyStatus, moonHubApi],
    );

    /**
     * Association path: the wallet is created under the live social session, so
     * no statement is signed and no proof is sent. Throws for the same reason
     * the EOA variant does: the ceremony reports which leg failed.
     */
    const startSocialWalletCreation = useCallback(async () => {
        if (!moonHubApi) {
            throw new Error('Sign in with your wallet to set up an account.');
        }
        const started = await createOnboardingWallet(moonHubApi, {
            anchorChainId,
        });
        applyStatus((current) => ({
            ...current,
            state:
                started.status === 'completed' ? 'completed' : 'provisioning',
            walletAddress: started.walletAddress,
            pollAfterMs: started.status === 'completed' ? 60_000 : 3_000,
        }));
    }, [anchorChainId, applyStatus, moonHubApi]);

    const ready = useCallback(async () => {
        if (!factionsApi || !moonHubApi) return;
        setBusy(true);
        setError(null);
        try {
            const admitted =
                statusRef.current.state === 'admitted'
                    ? statusRef.current
                    : await confirmOnboardingReady(factionsApi);
            applyStatus(admitted);
            // The call only starts the deployment; the queue status poll is what
            // reports it landing, so a slow chain never blocks the request.
            const started = await createOnboardingWallet(moonHubApi, {
                anchorChainId,
            });
            applyStatus({
                ...admitted,
                state:
                    started.status === 'completed'
                        ? 'completed'
                        : 'provisioning',
                walletAddress: started.walletAddress,
                pollAfterMs: started.status === 'completed' ? 60_000 : 3_000,
            });
        } catch (nextError) {
            setError(
                describeErrorText(nextError, { operation: 'Wallet setup' }),
            );
            await refreshStatus();
        } finally {
            setBusy(false);
        }
    }, [anchorChainId, applyStatus, factionsApi, moonHubApi, refreshStatus]);

    const rejoin = useCallback(async () => {
        if (!factionsApi) return;
        await perform(async () => rejoinOnboardingQueue(factionsApi));
    }, [factionsApi, perform]);

    return {
        config,
        configResolved,
        status,
        statusResolved:
            !active || !config.enabled || !factionsApi ? true : statusSettled,
        busy,
        error,
        redeem,
        ready,
        confirmReadyOnly,
        startEoaWalletCreation,
        startSocialWalletCreation,
        rejoin,
        refreshStatus,
    };
}
